import React, { createContext, useEffect, useMemo, useState } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import i18n from 'i18next';
import { initReactI18next, Trans } from 'react-i18next';

import { getResources } from '../../../locales/helper';

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

interface I18nContextValue {
  i18n: typeof i18n;
  t: typeof i18n.t;
  Trans: typeof Trans;
}
if (!i18n.isInitialized) {
  const resources = getResources();
  i18n.use(initReactI18next).init({
    // we initialise with resources
    resources,
    lng: localeConfig.defaultURLLocale, // Set initial language explicitly
    fallbackLng: localeConfig.defaultURLLocale,
    returnNull: false,
    returnEmptyString: false,
    // have a common namespace used around the full app
    ns: ['common'],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false, // Important for Next.js SSR
    },
  });
}
const I18nContext = createContext<I18nContextValue>({
  i18n,
  t: i18n.t,
  Trans,
});

function I18nContextProvider({
  children,
  languageRequested,
}: {
  children: React.ReactNode;
  languageRequested: string;
}): JSX.Element {
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  useEffect(() => {
    // Only change language if our i18n instance needs it
    if (i18n.language !== languageRequested) {
      // Since all resources are preloaded, change is synchronous
      // Update state immediately for instant UI update
      setCurrentLanguage(languageRequested);
      i18n.changeLanguage(languageRequested);
    }
  }, [languageRequested]);

  // Listen to i18next language change events to update state
  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', onLanguageChanged);

    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  return (
    <I18nContext.Provider
      value={useMemo(() => ({ t: i18n.t, Trans, i18n }), [currentLanguage])}
    >
      {children}
    </I18nContext.Provider>
  );
}

export default I18nContext;
export { I18nContextProvider };
